<template>
  <div class="comment-page-container">
    <!-- 1. 新增按钮：点击打开 Dialog -->
    <div class="action-bar" style="margin-bottom: 15px;">
      <el-button type="primary" icon="el-icon-map" @click="mapDialogVisible = true">
        查看评论地图
      </el-button>
    </div>

    <!-- 评论卡片区域 -->
    <el-row v-if="postId && postType">
      <el-col :span="12">
        <user-comment-card
          :post-id="postId"
          :post-type="postType"
          :current-user="currentUser"
          :screen-width="screenWidth"
        />
      </el-col>
    </el-row>

    <!-- 2. 新增 Dialog 弹窗嵌入地图代码 -->
    <el-dialog
      title="评论地区分布"
      :visible.sync="mapDialogVisible"
      width="70%"
      :append-to-body="true"
      @opened="handleMapDialogOpen"
    >
      <!-- 地图类型切换 Switch 区域 -->
      <div class="map-switch-bar" style="margin-bottom: 15px; display: flex; align-items: center; justify-content: flex-end;">
        <span style="margin-right: 10px; font-size: 14px; color: #606266;">地图类型：</span>
        <el-switch
          v-model="areaCode"
          :active-value="900000"
          :inactive-value="100000"
          active-text="全球地图"
          inactive-text="全国地图"
          @change="handleMapTypeChange"
        />
      </div>

      <div ref="mapContainer" class="chart-map-container">
        <div class="chart-wrapper">
          <div ref="chartsDOM" class="chart-content" style="width: 100%; height: 500px;" />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import UserCommentCard from '@/components/card/UserCommentCard.vue'
import * as echarts from 'echarts'
import { getCommentMap } from '@/api/vod'
import { getChartMapGeojson } from '@/api/geo'

export default {
  name: 'CommentPage',
  components: { UserCommentCard },
  data() {
    return {
      screenWidth: document.body.clientWidth,
      postId: null,
      postType: null,
      currentUser: { userId: -1, name: '游客', avatar: '', author: false },
      // 地图弹窗相关数据
      mapDialogVisible: false,
      myChart: null,
      areaCode: 100000, // 默认 100000 全国, 可切换为 900000 全球
      isLabelShow: false,
      loading: false
    }
  },
  created() {
    this.postId = this.$route.query.postId
    this.postType = this.$route.query.postType
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.myChart) {
      this.myChart.dispose()
    }
  },
  methods: {
    // 弹窗完全打开后再渲染/绘制地图
    handleMapDialogOpen() {
      this.getMapData()
      if (this.myChart) {
        this.myChart.resize()
      }
    },
    // Switch 切换触发
    handleMapTypeChange() {
      this.getMapData()
    },
    async getMapData() {
      this.loading = true

      if (!this.myChart) {
        this.myChart = echarts.init(this.$refs['chartsDOM'])
      }
      this.myChart.showLoading({ text: '地图数据加载中...' })

      try {
        // 使用 Promise.all 同时发起地图轮廓(GeoJSON)和业务数据(list)请求
        const [geoResp, commentResp] = await Promise.all([
          getChartMapGeojson(this.areaCode),
          getCommentMap({
            areaCode: this.areaCode,
            postId: this.postId
          })
        ])

        if (geoResp && geoResp.code === 0 && commentResp && commentResp.code === 0) {
          // 1. 解析 GeoJSON (来自 getChartMapGeojson)
          const rawGeoJson = geoResp.data
          const geoJson = typeof rawGeoJson === 'string' ? JSON.parse(rawGeoJson) : rawGeoJson

          // 2. 解析统计列表数据 (来自 getCommentMap)
          const dataList = commentResp.data.list || commentResp.data || []

          const mapName = String(this.areaCode)
          const splitList = [
            { start: 1000, end: 10000 },
            { start: 100, end: 1000 },
            { start: 80, end: 100 },
            { start: 50, end: 80 },
            { start: 20, end: 50 },
            { start: 1, end: 20 },
            { start: 0, end: 0 }
          ]

          // 3. 构建 Option 并注册渲染
          const chartOption = this.buildChartOption(mapName, dataList, splitList)
          echarts.registerMap(mapName, geoJson)
          this.myChart.setOption(chartOption, true)
        } else {
          this.$message.error('获取地图或评论数据失败')
        }
      } catch (err) {
        this.$message.error('加载地图数据失败')
        console.error('getMapData error:', err)
      } finally {
        this.loading = false
        if (this.myChart) {
          this.myChart.hideLoading()
        }
      }
    },
    buildChartOption(mapName, dataList, splitList) {
      return {
        backgroundColor: '#FFFFFF',
        tooltip: {
          trigger: 'item',
          formatter: '{b}<br/>{a}: {c}'
        },
        visualMap: {
          show: true,
          left: 30,
          bottom: 30,
          splitList: splitList,
          color: [
            '#d9363e',
            '#fa8c16',
            '#fadb14',
            '#52c41a',
            '#1890ff',
            '#722ed1',
            '#1f1f1f'
          ]
        },
        series: [
          {
            name: '数量',
            type: 'map',
            map: mapName,
            roam: true,
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 1
            },
            emphasis: {
              itemStyle: {
                areaColor: '#389e0d'
              }
            },
            label: {
              show: this.isLabelShow,
              color: '#333',
              fontSize: 11
            },
            data: dataList
          }
        ]
      }
    },
    handleResize() {
      if (this.myChart) {
        this.myChart.resize()
      }
    }
  }
}
</script>

<style scoped>
.chart-content {
  width: 100%;
  height: 500px;
}
</style>
